import { useEffect, useMemo, useState } from 'react';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { useTheme } from '@/hooks/useTheme';
import { API_BASE_URL, authFetch } from '@/services/api';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

// =============================================================================
// Ф6 «Інфоаналітика» — 3-day РДН indices + TOP-5 image cards + text headlines.
// Solid cards (fast). No AI brief, no station-curtailment chip (that is owner-
// operational, not public market info).
// =============================================================================

type CatKey = 'legal' | 'media';
interface Item { source: string; title: string; link: string | null; publishedAt: string | null; fetchedAt: string; imageUrl: string | null; description: string | null; }
interface Day3 { date: string; avg: number | null; min: number | null; max: number | null; peakHour: number | null; peakPrice: number | null; hours: { hour: number; p: number }[]; }

const CATS: Record<CatKey, { label: string; icon: string; accent: string }> = {
  legal: { label: 'Законодавство та регулювання', icon: 'ri-scales-3-line', accent: '#6366F1' },
  media: { label: 'Новини ринку та медіа', icon: 'ri-newspaper-line', accent: '#0D7A3C' },
};
const DAY_COLORS = ['#94A3B8', '#7FB2E6', '#E0A030'];
const fmt = (n: any) => (n == null ? '—' : Number(n).toLocaleString('uk-UA', { maximumFractionDigits: 0 }));
const dm = (d: string) => `${d.slice(8)}.${d.slice(5, 7)}`;
function ago(iso: string | null): string {
  if (!iso) return ''; const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0 || isNaN(ms)) return ''; const m = Math.floor(ms / 60000);
  if (m < 1) return 'щойно'; if (m < 60) return `${m} хв`;
  const h = Math.floor(m / 60); return h < 24 ? `${h} год` : `${Math.floor(h / 24)} дн`;
}

export default function InfoAnalyticsPage() {
  const { isDark } = useTheme();
  const theme = {
    card: isDark ? '#1A1A1A' : '#FFFFFF',
    inner: isDark ? '#121212' : '#F5F5F7',
    border: isDark ? 'rgba(255,255,255,0.08)' : '#E5E5E8',
    text: isDark ? '#E6E6E8' : '#15151A',
    muted: '#7C7C86',
    chip: isDark ? 'rgba(255,255,255,0.06)' : '#F0F0F3',
    accent: '#D12229',
  };

  const [items, setItems] = useState<Record<string, Item[]>>({});
  const [days3, setDays3] = useState<Day3[]>([]);
  const [date, setDate] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    authFetch(`${API_BASE_URL}/info/digest/briefs`).then((r) => r.json()).then((d) => setDate(d.date || '')).catch(() => {});
    authFetch(`${API_BASE_URL}/info/market-3day`).then((r) => r.json()).then((d) => setDays3(d.days || [])).finally(() => setLoading(false));
    (['legal', 'media'] as const).forEach((c) =>
      authFetch(`${API_BASE_URL}/info/digest/items?category=${c}&limit=16&days=14`).then((r) => r.json())
        .then((d) => setItems((p) => ({ ...p, [c]: d.items || [] }))).catch(() => {}));
  }, []);

  const dateLabel = useMemo(() => { if (!date) return ''; const [y, m, d] = date.split('-'); return `${d}.${m}.${y}`; }, [date]);

  const { chartData, keys } = useMemo(() => {
    const keys = days3.map((d) => dm(d.date));
    const byHour: Record<number, any> = {};
    days3.forEach((d, di) => d.hours.forEach((h) => { byHour[h.hour] = byHour[h.hour] || { hour: h.hour }; byHour[h.hour][keys[di]] = h.p; }));
    return { chartData: Object.values(byHour).sort((a: any, b: any) => a.hour - b.hour), keys };
  }, [days3]);

  // TOP-5 (image cards) across both themes; rest → text rows per theme.
  const top5 = useMemo(() => {
    const all = [...(items.legal || []).map((x) => ({ ...x, cat: 'legal' as CatKey })),
                 ...(items.media || []).map((x) => ({ ...x, cat: 'media' as CatKey }))]
      .filter((x) => x.imageUrl)
      .sort((a, b) => new Date(b.publishedAt || b.fetchedAt).getTime() - new Date(a.publishedAt || a.fetchedAt).getTime());
    return all.slice(0, 5);
  }, [items]);
  const topSet = useMemo(() => new Set(top5.map((x) => x.link || x.title)), [top5]);
  const rest = (cat: CatKey) => (items[cat] || []).filter((x) => !topSet.has(x.link || x.title));

  const Card = ({ children, className = '' }: any) => (
    <div className={`rounded-xl ${className}`} style={{ background: theme.card, border: `1px solid ${theme.border}` }}>{children}</div>
  );

  const TopCard = ({ it }: { it: Item & { cat: CatKey } }) => {
    const accent = CATS[it.cat].accent;
    return (
      <a href={it.link || '#'} target="_blank" rel="noopener noreferrer" className="block group rounded-xl overflow-hidden"
         style={{ background: theme.card, border: `1px solid ${theme.border}`, textDecoration: 'none' }}>
        <div className="relative w-full" style={{ height: 110, background: `linear-gradient(135deg, ${accent}22, ${accent}08)` }}>
          <div className="absolute inset-0 flex items-center justify-center" style={{ color: `${accent}66` }}><i className={CATS[it.cat].icon} style={{ fontSize: 26 }} /></div>
          {it.imageUrl && <img src={it.imageUrl} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />}
          <span className="absolute top-1.5 left-1.5 text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ background: 'rgba(0,0,0,0.55)', color: '#fff' }}>{it.source.split(' ')[0]}</span>
        </div>
        <div className="p-2.5">
          <div className="text-[12px] font-semibold leading-snug line-clamp-3 group-hover:underline" style={{ color: theme.text }}>{it.title}</div>
          <div className="text-[10px] mt-1.5" style={{ color: theme.muted }}>{ago(it.publishedAt || it.fetchedAt)} тому</div>
        </div>
      </a>
    );
  };

  const TextRow = ({ it, accent, first }: { it: Item; accent: string; first: boolean }) => {
    const showDesc = it.description && it.description.length > 20 && it.description !== it.title && !it.title.startsWith(it.description.slice(0, 30));
    return (
      <a href={it.link || '#'} target="_blank" rel="noopener noreferrer" className="block group" style={{ textDecoration: 'none' }}>
        <div className="flex items-start gap-2.5 py-2.5" style={{ borderTop: first ? 'none' : `1px solid ${theme.border}` }}>
          <span className="w-1.5 h-1.5 rounded-full shrink-0 mt-1.5" style={{ background: accent }} />
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-medium leading-snug group-hover:underline" style={{ color: theme.text }}>{it.title}</div>
            {showDesc && <div className="text-[11px] mt-0.5 leading-snug line-clamp-2" style={{ color: theme.muted }}>{it.description}</div>}
            <div className="text-[10px] mt-1" style={{ color: theme.muted }}>{it.source} · {ago(it.publishedAt || it.fetchedAt)} тому</div>
          </div>
        </div>
      </a>
    );
  };

  return (
    <DashboardLayout>
      <div className="max-w-[1500px] mx-auto px-3 sm:px-5 py-4 sm:py-5">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: theme.text }}>Інфоаналітика</h1>
            <div className="text-xs mt-0.5 flex items-center gap-1.5" style={{ color: theme.muted }}>
              <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: '#22C55E' }} />
              Енергоринок України · законодавство · медіа{dateLabel ? ` · ${dateLabel}` : ''}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center text-sm py-16" style={{ color: theme.muted }}>Завантаження…</div>
        ) : (
        <>
          {/* MARKET — 3-day РДН indices */}
          <Card className="p-4 sm:p-5 mb-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: '#E0A0301c', color: '#E0A030' }}><i className="ri-line-chart-line" style={{ fontSize: 15 }} /></div>
              <div className="text-sm font-bold" style={{ color: theme.text }}>Індекси РДН за добу · 3 доби</div>
              <span className="ml-auto text-[10px]" style={{ color: theme.muted }}>грн / МВт·год</span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-5">
              <div className="grid grid-cols-3 gap-2 h-full">
                {days3.map((d, i) => {
                  const prev = i > 0 ? days3[i - 1].avg : null;
                  const delta = (d.avg != null && prev) ? Math.round((d.avg - prev) / prev * 100) : null;
                  const today = i === days3.length - 1;
                  const span = (d.max != null && d.min != null && d.max > d.min) ? (d.max - d.min) : 0;
                  const pct = span ? Math.max(4, Math.min(96, Math.round((d.avg - d.min) / span * 100))) : 50;
                  const col = DAY_COLORS[i] || DAY_COLORS[2];
                  return (
                    <div key={d.date} className="rounded-lg p-3 flex flex-col" style={{ background: theme.inner, border: today ? `1px solid ${DAY_COLORS[2]}66` : `1px solid ${theme.border}` }}>
                      <div className="flex items-center gap-1.5 text-[10px] font-bold" style={{ color: today ? theme.text : theme.muted }}>
                        <span className="w-2 h-2 rounded-full" style={{ background: col }} />{dm(d.date)}
                        {today && <span className="ml-auto text-[8px] px-1 py-0.5 rounded font-semibold" style={{ background: `${DAY_COLORS[2]}22`, color: DAY_COLORS[2] }}>сьогодні</span>}
                      </div>
                      <div className="mt-2.5">
                        <div className="text-xl font-extrabold tabular-nums leading-none" style={{ color: theme.text }}>{fmt(d.avg)}</div>
                        <div className="flex items-center gap-1.5 mt-1">
                          <span className="text-[9px]" style={{ color: theme.muted }}>грн/МВт·год</span>
                          {delta != null && <span className="text-[9px] font-semibold" style={{ color: delta > 0 ? '#22C55E' : '#E0A030' }}>{delta > 0 ? '▲' : '▼'} {Math.abs(delta)}%</span>}
                        </div>
                      </div>
                      <div className="mt-auto pt-3">
                        <div className="flex justify-between text-[9px] tabular-nums mb-1" style={{ color: theme.text }}><span>{fmt(d.min)}</span><span>{fmt(d.max)}</span></div>
                        <div className="h-1.5 rounded-full relative" style={{ background: theme.border }}>
                          <div className="absolute top-1/2 -translate-y-1/2 rounded-full" style={{ left: `calc(${pct}% - 3px)`, width: 6, height: 6, background: col }} />
                        </div>
                        <div className="flex justify-between text-[8px] mt-0.5" style={{ color: theme.muted }}><span>мін</span><span>макс</span></div>
                        {d.peakHour != null && <div className="flex items-center gap-1 text-[9px] mt-2 pt-2" style={{ color: theme.muted, borderTop: `1px solid ${theme.border}` }}><i className="ri-flashlight-line" style={{ color: '#E0A030' }} />пік {d.peakHour}:00 · <span className="tabular-nums" style={{ color: theme.text }}>{fmt(d.peakPrice)}</span></div>}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{ height: 200 }}>
                {chartData.length > 1 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 6, right: 8, left: -14, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={theme.border} vertical={false} />
                      <XAxis dataKey="hour" tick={{ fontSize: 9, fill: theme.muted }} axisLine={false} tickLine={false} tickFormatter={(h: number) => `${h}:00`} interval={2} />
                      <YAxis tick={{ fontSize: 9, fill: theme.muted }} axisLine={false} tickLine={false} width={40} tickFormatter={(v: number) => (v >= 1000 ? `${(v / 1000).toFixed(1)}K` : `${v}`)} />
                      <Tooltip contentStyle={{ background: theme.inner, border: `1px solid ${theme.border}`, borderRadius: 8, fontSize: 12, color: theme.text }} formatter={(v: number, n: string) => [`${fmt(v)} грн`, n]} labelFormatter={(l) => `${l}:00`} />
                      {keys.map((k, i) => <Line key={k} type="monotone" dataKey={k} stroke={DAY_COLORS[i] || DAY_COLORS[2]} strokeWidth={i === keys.length - 1 ? 2.6 : 1.6} dot={false} isAnimationActive={false} />)}
                    </LineChart>
                  </ResponsiveContainer>
                ) : <div className="flex items-center justify-center h-full text-xs" style={{ color: theme.muted }}>немає даних</div>}
                <div className="flex items-center justify-center gap-4 mt-1">
                  {keys.map((k, i) => <span key={k} className="flex items-center gap-1.5 text-[10px]" style={{ color: theme.muted }}><span className="w-3 h-0.5 rounded" style={{ background: DAY_COLORS[i] || DAY_COLORS[2] }} />{k}</span>)}
                </div>
              </div>
            </div>
          </Card>

          {/* TOP-5 events with images */}
          {top5.length > 0 && (
            <div className="mb-5">
              <div className="flex items-center gap-2 mb-3">
                <i className="ri-fire-line" style={{ color: theme.accent }} />
                <div className="text-sm font-bold" style={{ color: theme.text }}>Головні події</div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {top5.map((it, j) => <TopCard key={j} it={it} />)}
              </div>
            </div>
          )}

          {/* rest — text headlines per theme */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {(['legal', 'media'] as CatKey[]).map((cat) => {
              const meta = CATS[cat]; const list = rest(cat);
              return (
                <Card key={cat} className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${meta.accent}1c`, color: meta.accent }}><i className={meta.icon} style={{ fontSize: 15 }} /></div>
                    <div className="text-sm font-bold" style={{ color: theme.text }}>{meta.label}</div>
                    {list.length ? <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: theme.chip, color: theme.muted }}>{list.length}</span> : null}
                  </div>
                  {list.length === 0 ? <div className="text-xs py-4 text-center" style={{ color: theme.muted }}>Немає свіжих подій</div>
                    : list.map((it, j) => <TextRow key={j} it={it} accent={meta.accent} first={j === 0} />)}
                </Card>
              );
            })}
          </div>
        </>
        )}
      </div>
    </DashboardLayout>
  );
}
